import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import './CompanyPage.css';
import { apiService } from '../../services/apiService';
import notificationService from '../../CommanService/notificationService';
import { hasPermission, PERMISSION_CODES } from '../../utils/permissionUtils';
import { PermissionGuard } from '../../components/PermissionGuard';
import { apiCallWithCache, clearCacheEntry } from '../../utils/apiUtils';

const CompanyDetailModal = ({ company, onClose }) => {
  if (!company) return null;
  return (
    <div className="company-modal-overlay">
      <div className="company-modal-card">
        <div className="company-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px 18px 32px', position: 'relative', background: '#3a3323', color: '#fff', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
          <span className={`status-badge ${company.is_active ? 'active' : 'inactive'}`}>{company.is_active ? 'Active' : 'Inactive'}</span>
          <span className="company-modal-title" style={{ flex: 1, textAlign: 'center', fontSize: '2rem', fontWeight: 700, marginLeft: '-32px' }}>{company.company_name}</span>
          <button className="company-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="company-modal-body">
          <div className="company-modal-info">
            <div>
              <div className="company-modal-label">Email</div>
              <div>{company.company_email}</div>
            </div>
            <div>
              <div className="company-modal-label">Phone</div>
              <div>{company.company_phone_number}</div>
            </div>
            <div>
              <div className="company-modal-label">Company Address</div>
              <div>{company.company_address}</div>
            </div>
            <div>
              <div className="company-modal-label">Created On</div>
              <div>{company.created_on ? company.created_on.split('T')[0] : ''}</div>
            </div>
            <div>
              <div className="company-modal-label">Created By</div>
              <div>{company.created_by_name}</div>
            </div>
            <div>
              <div className="company-modal-label">Modified On</div>
              <div>{company.last_modified_on ? company.last_modified_on.split('T')[0] : ''}</div>
            </div>
            <div>
              <div className="company-modal-label">Modified By</div>
              <div>{company.last_modified_by_name}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddCompanyModal = ({ open, onClose, onSuccess, company }) => {
  const [form, setForm] = useState({
    companyName: '',
    companyAddress: '',
    companyEmail: '',
    phoneNumber: '',
    status: '',
    id: 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (company) {
      setForm({
        companyName: company.company_name || '',
        companyAddress: company.company_address || '',
        companyEmail: company.company_email || '',
        phoneNumber: company.company_phone_number || '',
        status: company.is_active ? 'Active' : 'Inactive',
        id: company.id || 0,
      });
    } else {
      setForm({ companyName: '', companyAddress: '', companyEmail: '', phoneNumber: '', status: 'Active', id: 0 });
    }
  }, [company, open]);

  if (!open) return null;
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const newErrors = {};
    if (!form.companyName) newErrors.companyName = 'Required';
    if (!form.companyAddress) newErrors.companyAddress = 'Required';
    if (!form.companyEmail) newErrors.companyEmail = 'Required';
    if (!form.status) newErrors.status = 'Required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      const firstName = sessionStorage.getItem('first_name') || '';
      const payload = {
        is_active: form.status === 'Active',
        id: form.id || 0,
        company_name: form.companyName,
        company_address: form.companyAddress,
        company_email: form.companyEmail,
        company_phone_number: form.phoneNumber,
        created_by_name: firstName,
        last_modified_by_name: firstName,
      };
      try {
        const response = await apiService.AddAndUpdateCompany(payload);
        console.log('AddAndUpdateCompany response:', response);
        console.log('Company saved successfully, calling onSuccess callback...');
        if (onSuccess) onSuccess();
        setForm({ companyName: '', companyAddress: '', companyEmail: '', phoneNumber: '', status: '', id: 0 });
        onClose();
        if (form.id && form.id !== 0) {
          notificationService.showSuccess('Company updated successfully!');
        } else {
          notificationService.showSuccess('Company added successfully!');
        }
      } catch (err) {
        console.error('AddAndUpdateCompany error:', err);
        notificationService.showError('Failed to save company.');
      }
    }
  };
  return (
    <div className="company-modal-overlay">
      <div className="company-modal-card" style={{ maxWidth: 600 }}>
        <div className="company-modal-header" style={{ background: '#fff', color: '#222', paddingBottom: 0 }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>{company && company.id ? 'Edit Company' : 'Add Company'}</span>
          <button className="company-modal-close" onClick={onClose}>×</button>
        </div>
        <form className="add-company-form" onSubmit={handleSubmit}>
          <div className="add-company-fields">
            <div className="add-company-field">
              <label>Company Name<span style={{ color: 'red' }}>*</span></label>
              <input name="companyName" value={form.companyName} onChange={handleChange} />
              {errors.companyName && <div className="add-company-error">{errors.companyName}</div>}
            </div>
            <div className="add-company-field">
              <label>Company Address<span style={{ color: 'red' }}>*</span></label>
              <input name="companyAddress" value={form.companyAddress} onChange={handleChange} />
              {errors.companyAddress && <div className="add-company-error">{errors.companyAddress}</div>}
            </div>
            <div className="add-company-field">
              <label>Company Email<span style={{ color: 'red' }}>*</span></label>
              <input name="companyEmail" value={form.companyEmail} onChange={handleChange} />
              {errors.companyEmail && <div className="add-company-error">{errors.companyEmail}</div>}
            </div>
            <div className="add-company-field">
              <label>Phone Number</label>
              <input name="phoneNumber" value={form.phoneNumber} onChange={handleChange} minLength={10} maxLength={13} />
            </div>
            <div className="add-company-field">
              <label>Status<span style={{ color: 'red' }}>*</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 500, minWidth: 48 }}>
                  {form.status === 'Active' ? 'Active' : 'In Active'}
                </span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={form.status === 'Active'}
                    onChange={e => handleChange({ target: { name: 'status', value: e.target.checked ? 'Active' : 'Inactive' } })}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
              {errors.status && <div className="add-company-error">{errors.status}</div>}
            </div>
          </div>
          <div className="add-company-actions">
            <button type="button" className="add-company-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="add-company-save">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CompanyPage = () => {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const isMountedRef = useRef(true);

  // Get user from Redux store for permission checks
  const { user } = useSelector(state => state.auth);

  // Fetch companies function
  const fetchCompanies = async () => {
    if (!isMountedRef.current) return;
    
    setLoading(true);
    setError(null);
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      let response;
      if (user.is_super_admin) {
        response = await apiCallWithCache('GetAllCompanies', () => apiService.GetAllCompanies(true));
        if (isMountedRef.current) {
          if (response && response.data) {
            console.log('Setting companies data:', response.data);
            setCompanies(Array.isArray(response.data) ? response.data : []);
          } else {
            console.log('Companies already cached, skipping...');
          }
        }
      } else {
        response = await apiCallWithCache(`GetCompaniesById_${user.company_id}`, () => apiService.GetCompaniesById(user.company_id));
        if (isMountedRef.current) {
          if (response && response.data) {
            console.log('Setting company by ID data:', response.data);
            // Patch: If data.data is not an array, wrap it in an array
            if (Array.isArray(response.data)) {
              setCompanies(response.data);
            } else if (response.data) {
              setCompanies([response.data]);
            } else {
              setCompanies([]);
            }
          } else {
            console.log('Company by ID already cached, skipping...');
          }
        }
      }
      console.log('Companies API response:', response);
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to fetch companies');
        console.error('fetchCompanies error:', err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Function to reload companies with cache clearing
  const reloadCompanies = async () => {
    console.log('Reloading companies...');
    // Clear the cache for companies to force a fresh API call
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (user.is_super_admin) {
      clearCacheEntry('GetAllCompanies');
    } else if (user && user.company_id) {
      clearCacheEntry(`GetCompaniesById_${user.company_id}`);
    }
    await fetchCompanies();
    console.log('Companies reloaded successfully');
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchCompanies();
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Permission checks - moved after all hooks
  const canViewCompany = hasPermission(user, PERMISSION_CODES.COMPANY_VIEW);
  const canAddCompany = hasPermission(user, PERMISSION_CODES.COMPANY_ADD);
  const canEditCompany = hasPermission(user, PERMISSION_CODES.COMPANY_EDIT);
  const canDeleteCompany = hasPermission(user, PERMISSION_CODES.COMPANY_DELETE);
  const hasCompanyManagement = hasPermission(user, PERMISSION_CODES.COMPANY_MANAGEMENT);

  // Check if user has any company permissions
  const hasCompanyAccess = canViewCompany || canAddCompany || canEditCompany || canDeleteCompany || hasCompanyManagement;

  // If user has no company permissions, show access denied
  // if (!hasCompanyAccess) {
  //   return (
  //     <div className="company-container">
  //       <div style={{ textAlign: 'center', marginTop: 40, color: 'red' }}>
  //         Access Denied: You don't have permission to view this page.
  //       </div>
  //     </div>
  //   );
  // }

  const filteredCompanies = companies.filter(company =>
    (company.company_name?.toLowerCase().includes(search.toLowerCase()) ||
     company.company_email?.toLowerCase().includes(search.toLowerCase()) ||
     String(company.id).includes(search))
  );

  const paginatedCompanies = filteredCompanies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  const handlePrevPage = () => {
    setPage((prev) => Math.max(prev - 1, 0));
  };

  const handleNextPage = () => {
    const maxPage = Math.floor(filteredCompanies.length / rowsPerPage);
    setPage((prev) => (prev < maxPage ? prev + 1 : prev));
  };

  const handleDeleteCompany = async (id) => {
    try {
     const response = await apiService.DeleteCompanyByID(id);
     console.log('DeleteCompanyByID response:', response);
     if (response.data === false) {
      console.log('Company already assigned to user, calling reloadCompanies...');
      await reloadCompanies();
      notificationService.showSuccess('Company already Assigned to User.');

     }  else if (response.success === true) {
      console.log('Company deleted successfully, calling reloadCompanies...');
      await reloadCompanies();
      notificationService.showSuccess('Company deleted successfully!');
     }
      else {
      notificationService.showError('Failed to delete company.');
     }
    } catch (err) {
      console.error('DeleteCompanyByID error:', err);
      notificationService.showError('Failed to delete company.');
    }
  };

  return (
    <div>
    <div className="company-header-bar">
        <form className="company-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </form>
        {canAddCompany && (
          <button className="add-company-btn" onClick={() => { setAddModalOpen(true); setEditCompany(null); }}>+ Add Company</button>
        )}
      </div>
    <div className="company-container">
      
      {loading ? (
        <div style={{ textAlign: 'center', marginTop: 40 }}>Loading...</div>
      ) : error ? (
        <div style={{ color: 'red', textAlign: 'center', marginTop: 40 }}>{error}</div>
      ) : (
        <table className="company-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Created</th>
              {(canViewCompany || canEditCompany || canDeleteCompany) && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedCompanies.map(company => (
              <tr key={company.id}>
                <td>{company.id}</td>
                <td>{company.company_name}</td>
                <td>{company.company_email}</td>
                <td>
                  <span className={`status-badge ${company.is_active ? 'active' : 'inactive'}`}>{company.is_active ? 'Active' : 'In Active'}</span>
                </td>
                <td>{company.created_on ? company.created_on.split('T')[0] : ''}</td>
                {(canViewCompany || canEditCompany || canDeleteCompany) && (
                  <td>
                    {canEditCompany && (
                      <span className="action-icon" title="Edit" onClick={() => { setEditCompany(company); setAddModalOpen(true); }}>✏️</span>
                    )}
                     {canViewCompany && (
                      <span className="action-icon" title="View" onClick={() => { setSelectedCompany(company); setModalOpen(true); }}>👁️</span>
                    )}
                    {canDeleteCompany && (
                      <span className="action-icon" title="Delete" onClick={() => handleDeleteCompany(company.id)}>🗑️</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="company-pagination">
        <div className="pagination-left">
          <label>
            Rows per page:
            <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
        <div className="pagination-center">
          {filteredCompanies.length === 0
            ? '0'
            : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, filteredCompanies.length)}`
          } of {filteredCompanies.length}
        </div>
        <div className="pagination-right">
          <button onClick={handlePrevPage} disabled={page === 0}>{'<'}</button>
          <button onClick={handleNextPage} disabled={(page + 1) * rowsPerPage >= filteredCompanies.length}>{'>'}</button>
        </div>
      </div>
      <AddCompanyModal open={addModalOpen} onClose={() => { setAddModalOpen(false); setEditCompany(null); }} onSuccess={reloadCompanies} company={editCompany} />
      <CompanyDetailModal company={selectedCompany} onClose={() => { setModalOpen(false); setSelectedCompany(null); }} />
    </div>
    </div>
  );
};

export default CompanyPage; 